import React from 'react'
import {AlternatingTextImage as AlternatingTextImageType, MarketingFormCmsContent, TextPosition, TextSizeOrSpacing} from '../../types/cms'
import {Box, Button, Flex, Stack, Text, useDisclosure} from '../../vanilla'
import {CTAButton} from '../../vanilla/components/CTAButton'
import {BonusCalendarIcon} from '../icons'
import {CmsImage} from './cms-image'
import {Markdown} from './Markdown'
import {MarketingForm} from './marketing-form'
import { CONTENT_SCHEMAS } from './utils'
import { CompetitionMarketingForm } from './competition-marketing-form'
import { CompetitionMarketingFormCmsContent } from '../../types/cms/competition-marketing-form'

export interface AlternatingTextImageProps {
  content: AlternatingTextImageType
}

// add more icons to this as more are added to the schema
const iconMapping = {
  'bonus-calendar': BonusCalendarIcon,
}

export const AlternatingTextImage = ({content}: AlternatingTextImageProps) => {
  const marketingFormModal = useDisclosure(false)
  if (!content) {
    return null
  }
  const {image, text, textControls, cta, marketingModal} = content
  const marketingForm = marketingModal?.marketingForm
  let CtaIcon
  if (marketingModal?.badgeIcon) {
    CtaIcon = iconMapping[marketingModal.badgeIcon as 'bonus-calendar']
  }
  const textSpacing = {
    sm:
      textControls?.textAreaSpacing === TextSizeOrSpacing.SMALL ||
      textControls?.textAreaSpacing === TextSizeOrSpacing.NONE ||
      !!textControls?.textAreaSpacing,
    md: textControls?.textAreaSpacing === TextSizeOrSpacing.MEDIUM,
    lg: textControls?.textAreaSpacing === TextSizeOrSpacing.LARGE,
  }

  return (
    <>
      <Flex
        direction={['column-reverse', image?.imageOnRight ? 'row' : 'row-reverse']}
        gap={['12px', textSpacing.lg ? '80px' : textSpacing.md ? '64px' : '48px']}
        alignItems={textControls?.textPosition === TextPosition.MIDDLE ? 'center' : 'flex-start'}
      >
        <Stack spacing="16px" flex="1">
          <Box>
            <Text variant="heading5" lineHeight="normal">
              {text.headline}
            </Text>
            {text?.eyebrow && <Text variant="heading0">{text.eyebrow}</Text>}
          </Box>
          <Markdown
            markdown={(text.body || '') as string}
            componentProps={{
              p: {
                variant: 'text3',
                lineHeight: 'short',
              },
              a: {
                variant: 'text3',
                textDecoration: 'underline',
                lineHeight: 'short',
              }
            }}
          />
          {cta?.ctaText ? (
            <CTAButton cta={cta} style={{width: 'fit-content'}} />
          ) : marketingModal?.ctaText ? (
            <Button
              variant="blueLink"
              style={{width: 'fit-content', height: 'fit-content'}}
              onClick={marketingFormModal.onToggle}
              {...(marketingModal?.badgeIcon &&
                CtaIcon && {
                  iconLeft: <CtaIcon />,
                })}
            >
              {marketingModal.ctaText}
            </Button>
          ) : null}
        </Stack>
        <Box flex="1">
          <CmsImage
            queryParams={[
              {w: 550, fmt: 'auto', qlt: 'default'},
              {w: 1100, fmt: 'auto', qlt: 'default'},
            ]}
            images={image?.image?.images}
            width="full"
            style={{objectFit: 'cover', minHeight: 260}}
          />
        </Box>
      </Flex>
      {marketingForm && marketingForm._meta.schema === CONTENT_SCHEMAS.MARKETING_FORM ? (
        <MarketingForm content={marketingForm as MarketingFormCmsContent} isModal {...marketingFormModal} />
      ) : marketingForm && marketingForm._meta.schema === CONTENT_SCHEMAS.COMPETITION_MARKETING_FORM ? (
        <CompetitionMarketingForm content={marketingForm as CompetitionMarketingFormCmsContent} formType='Modal' {...marketingFormModal}/>
      )
      : null}
    </>
  )
}
